import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save,
  X,
  Plus,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Briefcase,
  Shield,
  DollarSign,
  FileText,
  Star,
  Award,
  Settings,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Target,
  Users,
  BarChart3,
  Upload,
  Link,
  Tag,
  Database
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import SubcontractingService from '../../services/subcontractingService';

// Helper function to map subcontracting data to form data
const mapSubcontractingToFormData = (subcontracting) => {
  if (!subcontracting) return null;

  // Map business type from legal entity type
  const businessTypeMap = {
    'Corporation': 'corporation',
    'LLC': 'llc',
    'Partnership': 'partnership',
    'Sole Proprietorship': 'sole-proprietorship',
    'Non-Profit': 'non-profit'
  };

  // Map payment terms
  const paymentTermsMap = {
    'Net 15': 'net-15',
    'Net 30': 'net-30',
    'Net 45': 'net-45',
    'Net 60': 'net-60',
    'Due on Receipt': 'due-on-receipt'
  };

  // Map preferred communication
  const communicationMap = {
    'Email': 'email',
    'Phone': 'phone',
    'SMS': 'sms',
    'Portal': 'portal'
  };

  // Extract specialties and services from serviceCategories
  // We'll use all serviceCategories for both specialties and services
  const serviceCategories = subcontracting.serviceCategories?.map(cat => cat.category).filter(Boolean) || [];
  const specialties = serviceCategories;
  const services = serviceCategories; // Services are stored in the same serviceCategories array

  // Extract certifications - convert to strings for simple display
  // The form uses a simple string array for certifications
  const certifications = subcontracting.compliance?.certifications?.map(cert => {
    // If it's already a string, return it
    if (typeof cert === 'string') {
      return cert;
    }
    // If it's an object, return the name or a formatted string
    if (cert && typeof cert === 'object') {
      return cert.name || `${cert.issuer || ''} ${cert.certificateNumber || ''}`.trim() || 'Certification';
    }
    return '';
  }).filter(Boolean) || [];

  // Extract financial rates from businessDetails - handle 0 values correctly
  const hourlyRate = (subcontracting.businessDetails?.hourlyRate !== undefined && subcontracting.businessDetails?.hourlyRate !== null)
    ? String(subcontracting.businessDetails.hourlyRate)
    : '';
  const dailyRate = (subcontracting.businessDetails?.dailyRate !== undefined && subcontracting.businessDetails?.dailyRate !== null)
    ? String(subcontracting.businessDetails.dailyRate)
    : '';

  // Extract settings from metadata
  const metadata = subcontracting.metadata || {};
  const notificationSettings = metadata.notificationSettings || {
    emailNotifications: true,
    smsNotifications: false,
    projectUpdates: true,
    paymentNotifications: true
  };
  const preferredCommunication = metadata.preferredCommunication || communicationMap[subcontracting.communication?.preferredMethod] || 'email';
  const availability = metadata.availability || (subcontracting.status === 'Active' ? 'available' : 'unavailable');
  const workingHours = metadata.workingHours || '';
  const travelWillingness = metadata.travelWillingness || 'yes';
  const remoteWork = metadata.remoteWork || 'yes';
  const autoApproval = metadata.autoApproval !== undefined ? metadata.autoApproval : false;

  return {
    // Basic Information
    companyName: subcontracting.name || '',
    contactPerson: subcontracting.primaryContact?.name || '',
    email: subcontracting.primaryContact?.email || subcontracting.communication?.email || '',
    phone: subcontracting.primaryContact?.phone || subcontracting.communication?.phone || '',
    website: subcontracting.communication?.website || '',
    address: subcontracting.addresses?.headquarters?.street || '',
    city: subcontracting.addresses?.headquarters?.city || '',
    state: subcontracting.addresses?.headquarters?.state || '',
    zipCode: subcontracting.addresses?.headquarters?.postalCode || '',
    country: subcontracting.addresses?.headquarters?.country || subcontracting.businessRegistration?.incorporationCountry || '',
    businessType: businessTypeMap[subcontracting.businessRegistration?.legalEntityType] || '',
    industry: subcontracting.industry || subcontracting.sector || '',
    companySize: '', // Not in subcontracting schema
    yearsInBusiness: '', // Not in subcontracting schema
    taxId: subcontracting.businessRegistration?.taxId || '',
    registrationNumber: subcontracting.businessRegistration?.registrationNumber || '',

    // Company Details
    description: subcontracting.performance?.notes || '',
    specialties: specialties,
    services: services, // Now fetched from serviceCategories
    capabilities: [], // Not in subcontracting schema
    equipment: [], // Not in subcontracting schema
    certifications: certifications,
    licenses: [], // Not in subcontracting schema

    // Financial Information
    hourlyRate: hourlyRate, // Now fetched from businessDetails
    dailyRate: dailyRate, // Now fetched from businessDetails
    currency: subcontracting.businessDetails?.currency || 'USD',
    paymentTerms: paymentTermsMap[subcontracting.businessDetails?.paymentTerms] || '',
    billingAddress: subcontracting.addresses?.billing?.street || '',
    bankDetails: {
      bankName: subcontracting.banking?.bankName || '',
      accountNumber: subcontracting.banking?.accountNumber || '',
      routingNumber: subcontracting.banking?.routingNumber || '',
      swiftCode: subcontracting.banking?.swiftCode || ''
    },

    // Project History
    previousProjects: [], // Not in subcontracting schema
    references: [], // Not in subcontracting schema
    portfolio: [], // Not in subcontracting schema

    // Compliance & Security
    insuranceDetails: {
      generalLiability: subcontracting.compliance?.insurance?.policyNumber || '',
      workersCompensation: '', // Not in subcontracting schema
      professionalLiability: '', // Not in subcontracting schema
      expirationDate: subcontracting.compliance?.insurance?.expiryDate ? new Date(subcontracting.compliance.insurance.expiryDate).toISOString().split('T')[0] : ''
    },
    backgroundChecks: [], // Not in subcontracting schema
    securityClearance: '', // Not in subcontracting schema
    complianceRequirements: [], // Not in subcontracting schema

    // Preferences & Settings
    preferredCommunication: preferredCommunication, // Now fetched from metadata
    workingHours: workingHours, // Now fetched from metadata
    availability: availability, // Now fetched from metadata
    travelWillingness: travelWillingness, // Now fetched from metadata
    remoteWork: remoteWork, // Now fetched from metadata
    languagePreferences: [], // Not in subcontracting schema
    notificationSettings: notificationSettings, // Now fetched from metadata

    // Advanced Settings
    autoApproval: autoApproval, // Now fetched from metadata
    preferredProjects: [],
    blacklistedProjects: [],
    customFields: []
  };
};

const ArrayInput = ({
  label,
  values,
  placeholder,
  disabled,
  onAdd,
  onRemove
}) => {
  const inputRef = React.useRef(null);

  const handleAdd = () => {
    const value = inputRef.current?.value?.trim();
    if (!value) return;
    onAdd(value);
    inputRef.current.value = '';
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      <div className="array-input-group">
        {values.map((item, index) => (
          <div key={index} className="array-input-item">
            <input
              type="text"
              className="form-control"
              value={item}
              disabled={disabled}
              onChange={(e) => {
                const updated = [...values];
                updated[index] = e.target.value;
                onAdd(updated, true);
              }}
            />
            {!disabled && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => onRemove(index)}
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}

        {!disabled && (
          <div className="array-input-add">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder={placeholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // 🚨 critical
                  handleAdd();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAdd}
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AddContractor = ({ editMode = false, viewMode = false, vendorId = null, initialData = null }) => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // Tab management
  const [activeTab, setActiveTab] = useState('basic');

  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [loading, setLoading] = useState(editMode || viewMode);

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    businessType: '',
    industry: '',
    companySize: '',
    yearsInBusiness: '',
    taxId: '',
    registrationNumber: '',

    // Company Details
    description: '',
    specialties: [],
    services: [],
    capabilities: [],
    equipment: [],
    certifications: [],
    licenses: [],

    // Financial Information
    hourlyRate: '',
    dailyRate: '',
    currency: 'USD',
    paymentTerms: '',
    billingAddress: '',
    bankDetails: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: ''
    },

    // Project History
    previousProjects: [],
    references: [],
    portfolio: [],

    // Compliance & Security
    insuranceDetails: {
      generalLiability: '',
      workersCompensation: '',
      professionalLiability: '',
      expirationDate: ''
    },
    backgroundChecks: [],
    securityClearance: '',
    complianceRequirements: [],

    // Preferences & Settings
    preferredCommunication: 'email',
    workingHours: '',
    availability: 'available',
    travelWillingness: 'yes',
    remoteWork: 'yes',
    languagePreferences: [],
    notificationSettings: {
      emailNotifications: true,
      smsNotifications: false,
      projectUpdates: true,
      paymentNotifications: true
    },

    // Advanced Settings
    autoApproval: false,
    preferredProjects: [],
    blacklistedProjects: [],
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get disabled/readOnly props for form inputs
  const getInputProps = () => ({
    disabled: viewMode,
    readOnly: viewMode
  });

  // Load subcontracting data if in edit or view mode
  useEffect(() => {
    const loadSubcontractingData = async () => {
      if (vendorId) {
        setLoading(true);
        try {
          const response = await SubcontractingService.getSubcontracting(vendorId);
          if (response && response.success) {
            const mappedData = mapSubcontractingToFormData(response.data);
            if (mappedData) {
              setFormData(mappedData);
            }
          } else {
            showError('Failed to load subcontractor details.');
            navigate('/subcontracting/management');
          }
        } catch (err) {
          console.error('Error loading subcontractor:', err);
          showError('Failed to load subcontractor details.');
          navigate('/subcontracting/management');
        } finally {
          setLoading(false);
        }
      } else if (initialData) {
        // Use provided initial data
        const mappedData = mapSubcontractingToFormData(initialData);
        if (mappedData) {
          setFormData(mappedData);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    loadSubcontractingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // Load test mode setting on component mount (only if not in edit/view mode)
  useEffect(() => {
    if (!editMode && !viewMode) {
      const loadTestMode = async () => {
        try {
          const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
            headers: API_CONFIG.HEADERS
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              setTestMode(result.data.testMode ?? false);
            }
          }
        } catch (error) {
          console.error('Error loading test mode setting:', error);
        }
      };

      loadTestMode();
    }
  }, [editMode, viewMode]);

  // Function to fill form with test data
  const fillTestData = () => {
    const testData = {
      // Basic Information
      companyName: 'Premier Construction Services Inc.',
      contactPerson: 'John Mitchell',
      email: 'john.mitchell@premierconstruction.com',
      phone: '+1 (555) 123-4567',
      website: 'https://www.premierconstruction.com',
      address: '1234 Industrial Boulevard, Suite 500',
      city: 'Los Angeles',
      state: 'California',
      zipCode: '90001',
      country: 'US',
      businessType: 'corporation',
      industry: 'construction',
      companySize: '201-500',
      yearsInBusiness: '15',
      taxId: '12-3456789',
      registrationNumber: 'CA-2024-789456',

      // Company Details
      description: 'Premier Construction Services is a leading general contractor specializing in commercial and residential construction projects. With over 15 years of experience, we deliver high-quality construction services with a focus on sustainability, innovation, and client satisfaction.',
      specialties: ['Commercial Construction', 'Residential Development', 'Renovation & Remodeling', 'Project Management'],
      services: ['General Contracting', 'Design-Build', 'Construction Management', 'Pre-Construction Services', 'Post-Construction Services'],
      capabilities: ['Large-Scale Projects', 'LEED Certified Construction', 'Smart Building Integration', 'Sustainable Construction'],
      equipment: ['Heavy Machinery', 'Crane Services', 'Excavation Equipment', 'Concrete Mixing'],
      certifications: ['LEED Gold Certified', 'OSHA 30-Hour', 'EPA Lead-Safe Certified', 'State Licensed General Contractor'],
      licenses: ['CA General Contractor License #12345', 'CA Electrical License #E-67890', 'CA Plumbing License #P-11111'],

      // Financial Information
      hourlyRate: '125.00',
      dailyRate: '1000.00',
      currency: 'USD',
      paymentTerms: 'net-30',
      billingAddress: '1234 Industrial Boulevard, Suite 500, Los Angeles, CA 90001',
      bankDetails: {
        bankName: 'First National Bank',
        accountNumber: '9876543210',
        routingNumber: '121000248',
        swiftCode: 'FNBOUS33'
      },

      // Project History
      previousProjects: [
        'Downtown Office Complex - 2023',
        'Luxury Residential Tower - 2022',
        'Shopping Mall Renovation - 2021',
        'Hospital Wing Expansion - 2020'
      ],
      references: [
        'ABC Development Corp - Contact: Sarah Johnson, (555) 234-5678',
        'Metro Real Estate Group - Contact: Michael Chen, (555) 345-6789',
        'Healthcare Facilities Inc - Contact: Dr. Emily Rodriguez, (555) 456-7890'
      ],
      portfolio: [],

      // Compliance & Security
      insuranceDetails: {
        generalLiability: '5000000',
        workersCompensation: '2000000',
        professionalLiability: '1000000',
        expirationDate: '2025-12-31'
      },
      backgroundChecks: ['All employees verified', 'Drug screening completed', 'Criminal background checks'],
      securityClearance: 'Standard',
      complianceRequirements: ['OSHA Compliance', 'State Building Codes', 'Environmental Regulations', 'Safety Standards'],

      // Preferences & Settings
      preferredCommunication: 'email',
      workingHours: '7:00 AM - 5:00 PM PST',
      availability: 'available',
      travelWillingness: 'yes',
      remoteWork: 'no',
      languagePreferences: ['English', 'Spanish'],
      notificationSettings: {
        emailNotifications: true,
        smsNotifications: true,
        projectUpdates: true,
        paymentNotifications: true
      },

      // Advanced Settings
      autoApproval: false,
      preferredProjects: [],
      blacklistedProjects: [],
      customFields: []
    };

    setFormData(testData);
    success('Test data filled successfully!');
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Company Info',
      icon: <Building size={18} />
    },
    {
      id: 'capabilities',
      label: 'Services',
      icon: <Briefcase size={18} />
    },
    {
      id: 'financial',
      label: 'Financial',
      icon: <DollarSign size={18} />
    },
    {
      id: 'preferences',
      label: 'Settings',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Don't submit in view mode
    if (viewMode) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.companyName || !formData.contactPerson || !formData.email || !formData.phone) {
        showError('Please fill in all required fields (Company Name, Contact Person, Email, and Phone).');
        setIsSubmitting(false);
        return;
      }

      let response;

      if (editMode && vendorId) {
        // Update existing contractor
        const subcontractingData = SubcontractingService.mapContractorToSubcontracting(formData);
        response = await SubcontractingService.updateSubcontracting(vendorId, subcontractingData);

        if (response && response.success) {
          success('Subcontractor updated successfully!');
          setTimeout(() => {
            navigate('/subcontracting/management');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to update subcontractor. Please try again.');
        }
      } else {
        // Create new contractor
        const subcontractingData = SubcontractingService.mapContractorToSubcontracting(formData);
        response = await SubcontractingService.createSubcontracting(subcontractingData);

        if (response && response.success) {
          success('Contractor added successfully!');
          setTimeout(() => {
            navigate('/subcontracting/management');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to add contractor. Please try again.');
        }
      }
    } catch (err) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} contractor:`, err);
      console.error('Error response data:', err?.response?.data);
      console.error('Error response errors:', err?.response?.data?.errors);

      // Handle error from service
      let errorMessage = `Failed to ${editMode ? 'update' : 'add'} contractor. Please try again.`;

      if (err?.response?.data) {
        const errorData = err.response.data;

        // Check for validation errors array
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map(e => {
            const field = e.param || e.path || '';
            const msg = e.msg || e.message || 'Invalid value';
            return field ? `${field}: ${msg}` : msg;
          });
          errorMessage = `Validation failed: ${validationErrors.join(', ')}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div>
            {renderBasicInformation()}
            {renderCompanyDetails()}
          </div>
        );
      case 'capabilities':
        return (
          <div>
            {renderCapabilitiesServices()}
            {renderComplianceCertifications()}
          </div>
        );
      case 'financial':
        return renderFinancialTerms();
      case 'preferences':
        return (
          <div>
            {renderProjectHistory()}
            {renderPreferencesSettings()}
          </div>
        );
      default:
        return (
          <div>
            {renderBasicInformation()}
            {renderCompanyDetails()}
          </div>
        );
    }
  };

  const renderBasicInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Contact Information</h3>
        <p className="section-description">Primary contact details and basic company information</p>
      </div>

      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Company Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Enter company name"
            required
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Contact Person *</label>
          <input
            type="text"
            className="form-control"
            value={formData.contactPerson}
            onChange={(e) => handleInputChange('contactPerson', e.target.value)}
            placeholder="Enter contact person name"
            required
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter email address"
            required
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Phone *</label>
          <input
            type="tel"
            className="form-control"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter phone number"
            required
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Website</label>
          <input
            type="url"
            className="form-control"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            placeholder="https://www.company.com"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Business Type</label>
          <select
            className="form-select"
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select business type</option>
            <option value="corporation">Corporation</option>
            <option value="llc">LLC</option>
            <option value="partnership">Partnership</option>
            <option value="sole-proprietorship">Sole Proprietorship</option>
            <option value="non-profit">Non-Profit</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Industry</label>
          <select
            className="form-select"
            value={formData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select industry</option>
            <option value="construction">Construction</option>
            <option value="technology">Technology</option>
            <option value="consulting">Consulting</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="healthcare">Healthcare</option>
            <option value="finance">Finance</option>
            <option value="education">Education</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Company Size</label>
          <select
            className="form-select"
            value={formData.companySize}
            onChange={(e) => handleInputChange('companySize', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select company size</option>
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
      </div>

      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Address</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="Enter full address"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief company description"
            {...getInputProps()}
          />
        </div>
      </div>
    </div>
  );

  const renderCompanyDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Company Details</h3>
        <p className="section-description">Business registration, location, and company background</p>
      </div>

      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">City</label>
          <input
            type="text"
            className="form-control"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Enter city"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">State/Province</label>
          <input
            type="text"
            className="form-control"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            placeholder="Enter state/province"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">ZIP/Postal Code</label>
          <input
            type="text"
            className="form-control"
            value={formData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            placeholder="Enter ZIP/postal code"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select country</option>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="UK">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Years in Business</label>
          <input
            type="number"
            className="form-control"
            value={formData.yearsInBusiness}
            onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
            placeholder="Enter years in business"
            min="0"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Tax ID</label>
          <input
            type="text"
            className="form-control"
            value={formData.taxId}
            onChange={(e) => handleInputChange('taxId', e.target.value)}
            placeholder="Enter tax ID"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Registration Number</label>
          <input
            type="text"
            className="form-control"
            value={formData.registrationNumber}
            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
            placeholder="Enter registration number"
            {...getInputProps()}
          />
        </div>
      </div>
    </div>
  );

  const renderCapabilitiesServices = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Services & Capabilities</h3>
        <p className="section-description">Define what services and capabilities the contractor offers</p>
      </div>

      <div className="form-group">
        <label className="form-label">Specialties</label>
        <ArrayInput
          values={formData.specialties}
          placeholder="Add specialty"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              specialties: isUpdate ? value : [...prev.specialties, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              specialties: prev.specialties.filter((_, i) => i !== index)
            }))
          }
        />
      </div>

      <div className="form-group">
        <label className="form-label">Services Offered</label>
        <ArrayInput
          values={formData.services}
          placeholder="Add service"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              services: isUpdate ? value : [...prev.services, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              services: prev.services.filter((_, i) => i !== index)
            }))
          }
        />
      </div>
    </div>
  );

  const renderComplianceCertifications = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Certifications & Compliance</h3>
        <p className="section-description">Professional certifications, licenses, and compliance requirements</p>
      </div>

      <div className="form-group">
        <label className="form-label">Certifications</label>
        <ArrayInput
          values={formData.certifications}
          placeholder="Add certification"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              certifications: isUpdate ? value : [...prev.certifications, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              certifications: prev.certifications.filter((_, i) => i !== index)
            }))
          }
        />
      </div>

      <div className="form-group">
        <label className="form-label">Licenses</label>
        <ArrayInput
          values={formData.licenses}
          placeholder="Add license"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              licenses: isUpdate ? value : [...prev.licenses, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              licenses: prev.licenses.filter((_, i) => i !== index)
            }))
          }
        />
      </div>
    </div>
  );

  const renderFinancialTerms = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Financial Terms & Billing</h3>
        <p className="section-description">Rates, payment terms, billing information, and financial compliance</p>
      </div>

      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Hourly Rate</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              value={formData.hourlyRate}
              onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              {...getInputProps()}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Daily Rate</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="number"
              className="form-control"
              value={formData.dailyRate}
              onChange={(e) => handleInputChange('dailyRate', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              {...getInputProps()}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Currency</label>
          <select
            className="form-select"
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
            {...getInputProps()}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CAD">CAD</option>
            <option value="AUD">AUD</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Payment Terms</label>
          <select
            className="form-select"
            value={formData.paymentTerms}
            onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select payment terms</option>
            <option value="net-15">Net 15</option>
            <option value="net-30">Net 30</option>
            <option value="net-45">Net 45</option>
            <option value="net-60">Net 60</option>
            <option value="due-on-receipt">Due on Receipt</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Billing Address</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.billingAddress}
          onChange={(e) => handleInputChange('billingAddress', e.target.value)}
          placeholder="Enter billing address"
          {...getInputProps()}
        />
      </div>
    </div>
  );

  const renderProjectHistory = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Project History & References</h3>
        <p className="section-description">Previous projects, client references, and work portfolio</p>
      </div>

      <div className="form-group">
        <label className="form-label">Previous Projects</label>
        <ArrayInput
          values={formData.previousProjects}
          placeholder="Add project"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              previousProjects: isUpdate ? value : [...prev.previousProjects, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              previousProjects: prev.previousProjects.filter((_, i) => i !== index)
            }))
          }
        />
      </div>

      <div className="form-group">
        <label className="form-label">References</label>
        <ArrayInput
          values={formData.references}
          placeholder="Add reference"
          disabled={viewMode}
          onAdd={(value, isUpdate) =>
            setFormData(prev => ({
              ...prev,
              references: isUpdate ? value : [...prev.references, value]
            }))
          }
          onRemove={(index) =>
            setFormData(prev => ({
              ...prev,
              references: prev.references.filter((_, i) => i !== index)
            }))
          }
        />
      </div>
    </div>
  );

  const renderPreferencesSettings = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Preferences & Settings</h3>
        <p className="section-description">Communication preferences, notification settings, and working preferences</p>
      </div>

      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Preferred Communication</label>
          <select
            className="form-select"
            value={formData.preferredCommunication}
            onChange={(e) => handleInputChange('preferredCommunication', e.target.value)}
            {...getInputProps()}
          >
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="sms">SMS</option>
            <option value="portal">Portal</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Working Hours</label>
          <input
            type="text"
            className="form-control"
            value={formData.workingHours}
            onChange={(e) => handleInputChange('workingHours', e.target.value)}
            placeholder="e.g., 9 AM - 5 PM EST"
            {...getInputProps()}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Availability</label>
          <select
            className="form-select"
            value={formData.availability}
            onChange={(e) => handleInputChange('availability', e.target.value)}
            {...getInputProps()}
          >
            <option value="available">Available</option>
            <option value="busy">Busy</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Notification Settings</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notificationSettings.emailNotifications}
              onChange={(e) => handleNestedInputChange('notificationSettings', 'emailNotifications', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Email Notifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notificationSettings.smsNotifications}
              onChange={(e) => handleNestedInputChange('notificationSettings', 'smsNotifications', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">SMS Notifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notificationSettings.projectUpdates}
              onChange={(e) => handleNestedInputChange('notificationSettings', 'projectUpdates', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Project Updates</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notificationSettings.paymentNotifications}
              onChange={(e) => handleNestedInputChange('notificationSettings', 'paymentNotifications', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Payment Notifications</label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title={viewMode ? `View Subcontractor${formData.companyName ? ` - ${formData.companyName}` : ''}` : editMode ? `Edit Subcontractor${formData.companyName ? ` - ${formData.companyName}` : ''}` : "Add New Contractor"}
        description={viewMode ? "View subcontractor details (Read-only)" : editMode ? "Edit subcontractor details" : "Register a new contractor with comprehensive details and capabilities"}
      />
      {loading ? (
        <div className="page-container">
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
          {/* Main Content */}
          <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
            <form onSubmit={handleSubmit} style={{ width: '100%' }} className={viewMode ? 'view-mode' : ''}>
              {/* Tab Navigation */}
              <div className="project-tabs">
                <div className="project-tabs-nav">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <div className="project-tab-icon">
                        {tab.icon}
                      </div>
                      <div className="project-tab-content">
                        <div className="project-tab-label">{tab.label}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="project-form-card">
                {renderTabContent()}
              </div>

              {/* Form Actions */}
              {!viewMode && (
                <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {testMode && !editMode && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={fillTestData}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#ffffff',
                          borderColor: '#d1d5db'
                        }}
                      >
                        <Database size={18} />
                        Fill Test Data
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => navigate('/subcontracting/management')}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || loading || viewMode}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          {editMode ? 'Updating...' : 'Adding Contractor...'}
                        </>
                      ) : (
                        <>
                          <Save size={18} />
                          {editMode ? 'Update Contractor' : 'Add Contractor'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {viewMode && (
                <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/management')}
                  >
                    Back to List
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/subcontracting/management/edit/${vendorId}`)}
                    disabled={!vendorId}
                  >
                    Edit Subcontractor
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {viewMode && (
        <style>{`
          .view-mode input,
          .view-mode select,
          .view-mode textarea {
            pointer-events: none;
            opacity: 0.7;
            background-color: #f3f4f6 !important;
            cursor: not-allowed;
          }
          .view-mode .array-input-add,
          .view-mode .btn-outline-danger {
            display: none !important;
          }
          .view-mode button[type="submit"] {
            display: none !important;
          }
        `}</style>
      )}
    </>
  );
};

export default AddContractor;
